import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, tap, map } from 'rxjs';
import { DialogService } from 'primeng/dynamicdialog';
import { ConfirmDialogComponent } from '../components';
import { emailValidator } from '../validators/email.validator';
import { AbstractControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { HttpClient } from '@angular/common/http';
import { API_URL } from 'src/assets/http/apis';
import { environment } from 'src/environments/environment';
import {
  IMember,
  ICreateMemberRequest,
  ICreateMemberResponse,
  IUpdateMemberRoleResponse,
  IDeleteMemberResponse,
  IGetAllMembersResponse,
} from '../interfaces/members.interface';
import { ResponseStatus } from 'src/app/core/enums';

@Injectable({ providedIn: 'root' })
export class MembersService {
  private membersSubject = new BehaviorSubject<IMember[]>([]);
  members$ = this.membersSubject.asObservable();

  constructor(
    private dialogService: DialogService,
    private translate: TranslateService,
    private http: HttpClient
  ) {}

  /**
   * @description Fetches all members from the API and updates local state
   * @returns {Observable<IMember[]>} The members from API
   */
  getAllMembers(): Observable<IMember[]> {
    const url = `${environment.apiUrl}${API_URL.MEMBERS.GET_ALL}`;

    return this.http.get<IGetAllMembersResponse>(url).pipe(
      map(response => {
        if (response.status === ResponseStatus.SUCCESS && response.data) {
          return Array.isArray(response.data)
            ? response.data.map(member => ({
                // Pending invitations carry no user id yet (HK-174 / IVDR-260).
                id: member.userId ?? '',
                email: member.email,
                roleId: member.role,
                status: member.status ?? 'Active',
                invitationId: member.invitationId,
                expiresAt: member.expiresAt,
              }))
            : [];
        }
        return [];
      }),
      tap(members => this.membersSubject.next(members))
    );
  }

  /**
   * @description Adds a member via API call (role omitted as optional)
   * @param {string} email - The email of the member
   * @returns {Observable<ICreateMemberResponse>} The API response
   */
  createMember(email: string): Observable<ICreateMemberResponse> {
    const requestBody: ICreateMemberRequest = {
      email,
    };
    const url = `${environment.apiUrl}${API_URL.MEMBERS.CREATE}`;
    return this.http.post<ICreateMemberResponse>(url, requestBody);
  }

  /**
   * @description Opens the invite dialog and makes API call to create member
   * @returns {Observable<ICreateMemberResponse>} The API response for component to handle
   */
  openInviteDialogWithApi(): Observable<ICreateMemberResponse> {
    return new Observable(observer => {
      const ref = this.dialogService.open(ConfirmDialogComponent, {
        header: this.translate.instant('modules.profile_settings.account.dialog.invite_member.title'),
        width: '450px',
        data: {
          message: '',
          confirmLabel: 'shared.actions.buttons.send_invite',
          cancelLabel: 'shared.actions.buttons.cancel',
          showInput: true,
          inputPlaceholder: 'modules.profile_settings.account.dialog.invite_member.input_placeholder',
        },
        closable: true,
        styleClass: 'custom-confirm-dialog',
      });

      ref?.onClose.subscribe((email: string | false) => {
        if (email && typeof email === 'string' && this.validateEmail(email)) {
          this.createMember(email).subscribe({
            next: response => {
              observer.next(response);
              observer.complete();
            },
            error: error => {
              observer.error(error);
            },
          });
        } else {
          observer.complete();
        }
      });
    });
  }

  /**
   * @description Validates the email
   * @param {string} email - The email to validate
   * @returns {boolean} boolean
   */
  private validateEmail(email: string): boolean {
    const validator = emailValidator();
    return !validator({ value: email } as AbstractControl);
  }

  /**
   * @description Updates a member's role via API call
   * @param {string} memberId - The ID of the member to update
   * @param {string} role - The new role for the member
   * @returns {Observable<IUpdateMemberRoleResponse>} The API response
   */
  updateMemberRole(memberId: string, roleId: number): Observable<IUpdateMemberRoleResponse> {
    const url = `${environment.apiUrl}${API_URL.MEMBERS.UPDATE_ROLE.replace('{userId}', memberId).replace(
      '{role}',
      roleId.toString()
    )}`;

    return this.http.put<IUpdateMemberRoleResponse>(url, {});
  }

  /**
   * @description Deletes a member via API call
   * @param {string} memberId - The ID of the member to delete
   * @returns {Observable<IDeleteMemberResponse>} The API response
   */
  deleteMember(memberId: string): Observable<IDeleteMemberResponse> {
    const url = `${environment.apiUrl}${API_URL.MEMBERS.DELETE.replace('{userId}', memberId).replace(
      '{memberId}',
      memberId
    )}`;

    return this.http.delete<IDeleteMemberResponse>(url);
  }

  /**
   * @description Sends a PUT request to update user specialty by its ID.
   * @param {number} specialtyId - The ID of the specialty to update.
   * @returns An Observable of type `void` is being returned.
   */
  updateMemberSpecialty(specialtyId: number): Observable<void> {
    return this.http.put<void>(
      `${environment.apiUrl}${API_URL.MEMBERS.UPDATE_SPECIALTY.replace('{specialtyId}', specialtyId.toString())}`,
      {}
    );
  }
}
